import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Home, Tag, TicketCheck, UserCircle2, Plus, LogOut } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/promo/NotificationBell";
import { FlashCreditsPill } from "@/components/promo/FlashCreditsWidgets";

/**
 * Mobile-first shell for /merchant/*. Uses a max-width container so it also
 * looks presentable on desktop but the primary target is 360×780. Phase 13c —
 * refreshed to the light design system: white surface, muted text, orange
 * accents for primary CTAs and active tabs.
 */
export function MerchantLayout({ children, showTabs = true, showHeader = true }) {
    const { logout, user } = useAuth();
    return (
        <div className="min-h-screen bg-surface" data-testid="merchant-shell">
            <div className="mx-auto flex min-h-screen max-w-[560px] flex-col bg-white">
                {showHeader && (
                    <header
                        className="sticky top-0 z-30 flex items-center justify-between border-b border-[var(--border-soft)] bg-white/95 px-4 py-3 backdrop-blur"
                        data-testid="merchant-topbar"
                    >
                        <Link to="/merchant/home" className="flex items-center gap-2" data-testid="merchant-topbar-logo">
                            <Logo size={44} className="sm:hidden" />
                            <Logo size={52} className="hidden sm:flex" />
                        </Link>
                        <div className="flex items-center gap-2">
                            <FlashCreditsPill />
                            <NotificationBell />
                            <div className="text-right leading-tight">
                                <div className="font-heading text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                                    Merchant
                                </div>
                                <div
                                    className="max-w-[120px] truncate font-heading text-xs font-semibold text-[var(--text-primary)]"
                                    data-testid="merchant-topbar-name"
                                >
                                    {user?.business_name || user?.full_name}
                                </div>
                            </div>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={logout}
                                className="h-9 w-9 text-[var(--text-muted)] hover:text-rose-600"
                                data-testid="merchant-logout-btn"
                            >
                                <LogOut size={16} />
                            </Button>
                        </div>
                    </header>
                )}
                <main
                    className={`flex-1 ${showTabs ? "pb-24" : ""}`}
                    data-testid="merchant-main"
                >
                    {children || <Outlet />}
                </main>
                {showTabs && <BottomTabs />}
            </div>
        </div>
    );
}

const TABS = [
    { to: "/merchant/home", icon: Home, label: "Home", testid: "tab-home" },
    { to: "/merchant/offers", icon: Tag, label: "Offers", testid: "tab-offers" },
    { to: "/merchant/offers/new", icon: Plus, label: "New", testid: "tab-new", primary: true },
    { to: "/merchant/coupons", icon: TicketCheck, label: "Coupons", testid: "tab-coupons" },
    { to: "/merchant/profile", icon: UserCircle2, label: "Profile", testid: "tab-profile" },
];

function BottomTabs() {
    const loc = useLocation();
    return (
        <nav
            className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-[560px] border-t border-[var(--border-soft)] bg-white/95 backdrop-blur"
            data-testid="merchant-bottom-tabs"
        >
            <ul className="flex items-stretch justify-between px-2">
                {TABS.map((t) => {
                    const active =
                        loc.pathname === t.to ||
                        (t.to !== "/merchant/offers/new" && loc.pathname.startsWith(t.to + "/")) ||
                        (t.to === "/merchant/offers" && loc.pathname.startsWith("/merchant/offers") && !loc.pathname.startsWith("/merchant/offers/new"));
                    const Icon = t.icon;
                    if (t.primary) {
                        return (
                            <li key={t.to} className="relative flex flex-1 justify-center">
                                <NavLink
                                    to={t.to}
                                    data-testid={t.testid}
                                    className="mb-2 mt-[-14px] flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand-orange)] text-white shadow-lg shadow-orange-500/30 transition hover:opacity-90"
                                >
                                    <Icon size={22} />
                                </NavLink>
                            </li>
                        );
                    }
                    return (
                        <li key={t.to} className="flex-1">
                            <NavLink
                                to={t.to}
                                data-testid={t.testid}
                                className={`flex h-16 flex-col items-center justify-center gap-1 font-heading text-[11px] font-medium transition ${active ? "text-[var(--brand-orange)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`}
                            >
                                <Icon size={18} />
                                {t.label}
                            </NavLink>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

export default MerchantLayout;
